"use client";

import { useEffect, useState } from "react";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Text } from "@visx/text";

interface BenchmarkBarChartProps {
  benchmarkName: string;
  beforeLabel: string;
  afterLabel: string;
  beforeValue: number;
  afterValue: number;
  gptValue?: number;
}

const WIDTH = 380;
const HEIGHT = 240;
const MARGIN = { top: 32, right: 20, bottom: 40, left: 20 };
const GPT_LABEL_ZONE = 90;

export function BenchmarkBarChart({
  benchmarkName,
  beforeLabel,
  afterLabel,
  beforeValue,
  afterValue,
  gptValue,
}: BenchmarkBarChartProps) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const hasGpt = gptValue != null && !isNaN(gptValue);
  const barsWidth = hasGpt ? innerWidth - GPT_LABEL_ZONE : innerWidth;

  const data = [
    { label: "Base", value: beforeValue },
    { label: "Fine-tuned", value: afterValue },
  ];

  const xScale = scaleBand({
    domain: data.map((d) => d.label),
    range: [0, barsWidth],
    padding: 0.45,
  });

  const allValues = [beforeValue, afterValue];
  if (gptValue != null) allValues.push(gptValue);
  const yMax = Math.min(
    Math.ceil((Math.max(...allValues) + 10) / 10) * 10,
    100,
  );
  const yScale = scaleLinear({
    domain: [0, yMax],
    range: [innerHeight, 0],
    nice: true,
  });

  const barWidth = xScale.bandwidth();

  const showGpt = hasGpt && animated;

  return (
    <div className="flex flex-col items-center">
      <p className="mb-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
        {benchmarkName}
      </p>
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="overflow-visible"
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* Grid lines */}
          {yScale.ticks(4).map((tick) => (
            <line
              key={tick}
              x1={0}
              x2={innerWidth}
              y1={yScale(tick)}
              y2={yScale(tick)}
              className="stroke-border/40"
              strokeDasharray="3,3"
            />
          ))}

          {/* Before bar */}
          <Bar
            x={xScale("Base")}
            y={animated ? yScale(beforeValue) : innerHeight}
            width={barWidth}
            height={animated ? innerHeight - yScale(beforeValue) : 0}
            rx={4}
            className="fill-muted-foreground/25 transition-all duration-700 ease-out"
          />

          {/* After bar */}
          <Bar
            x={xScale("Fine-tuned")}
            y={animated ? yScale(afterValue) : innerHeight}
            width={barWidth}
            height={animated ? innerHeight - yScale(afterValue) : 0}
            rx={4}
            className="fill-emerald-500/80 transition-all duration-700 ease-out dark:fill-emerald-400/80"
          />

          {/* GPT-5.4 horizontal reference line */}
          {showGpt && (() => {
            const gptY = yScale(gptValue!);
            const labelX = barsWidth + 12;
            return (
              <g>
                <line
                  x1={0}
                  x2={barsWidth + 8}
                  y1={gptY}
                  y2={gptY}
                  stroke="#a78bfa"
                  strokeWidth={1.5}
                  strokeDasharray="4,3"
                  opacity={0.7}
                />
                <Text
                  x={labelX}
                  y={gptY - 5}
                  textAnchor="start"
                  fontSize={10}
                  fontWeight={600}
                  fill="#a78bfa"
                >
                  {"GPT-5.4"}
                </Text>
                <Text
                  x={labelX}
                  y={gptY + 7}
                  textAnchor="start"
                  fontSize={9}
                  fontWeight={500}
                  fill="#a78bfa"
                  opacity={0.75}
                >
                  {`${gptValue!.toFixed(1)}%`}
                </Text>
              </g>
            );
          })()}

          {/* Animated border with travelling dot on the after bar */}
          {animated && (() => {
            const bx = (xScale("Fine-tuned") ?? 0) - 1;
            const by = yScale(afterValue) - 1;
            const bw = barWidth + 2;
            const bh = innerHeight - yScale(afterValue) + 2;
            const r = 5;
            // Rect path (clockwise from top-left, with rounded corners)
            const pathD = [
              `M ${bx + r} ${by}`,
              `L ${bx + bw - r} ${by}`,
              `Q ${bx + bw} ${by} ${bx + bw} ${by + r}`,
              `L ${bx + bw} ${by + bh - r}`,
              `Q ${bx + bw} ${by + bh} ${bx + bw - r} ${by + bh}`,
              `L ${bx + r} ${by + bh}`,
              `Q ${bx} ${by + bh} ${bx} ${by + bh - r}`,
              `L ${bx} ${by + r}`,
              `Q ${bx} ${by} ${bx + r} ${by}`,
              "Z",
            ].join(" ");
            const pathId = "after-bar-border";

            return (
              <>
                {/* Subtle static border */}
                <path
                  d={pathD}
                  fill="none"
                  className="stroke-emerald-400/20 dark:stroke-emerald-300/15"
                  strokeWidth={1}
                />

                <defs>
                  <path id={pathId} d={pathD} fill="none" />
                  {/* Glow filter for the dot */}
                  <filter id="spark-glow" x="-200%" y="-200%" width="500%" height="500%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Trail — faded, slightly larger dot that follows behind */}
                <circle r={4} className="fill-emerald-400/15 dark:fill-emerald-300/10">
                  <animateMotion
                    dur="4s"
                    repeatCount="indefinite"
                    keyPoints="0.98;0;0.98"
                    keyTimes="0;0.5;1"
                    calcMode="linear"
                  >
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                </circle>

                {/* Main bright dot */}
                <circle
                  r={2}
                  filter="url(#spark-glow)"
                  className="fill-emerald-300 dark:fill-emerald-200"
                  opacity={0.9}
                >
                  <animateMotion
                    dur="4s"
                    repeatCount="indefinite"
                  >
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                </circle>
              </>
            );
          })()}

          {/* Value labels above bars */}
          {animated && (
            <>
              <Text
                x={(xScale("Base") ?? 0) + barWidth / 2}
                y={yScale(beforeValue) - 10}
                textAnchor="middle"
                className="fill-muted-foreground text-xs font-medium"
                fontSize={13}
                fontWeight={600}
              >
                {beforeLabel}
              </Text>
              <Text
                x={(xScale("Fine-tuned") ?? 0) + barWidth / 2}
                y={yScale(afterValue) - 10}
                textAnchor="middle"
                className="fill-emerald-600 text-xs font-semibold dark:fill-emerald-400"
                fontSize={13}
                fontWeight={700}
              >
                {afterLabel}
              </Text>
            </>
          )}

          {/* X-axis labels */}
          {data.map((d) => (
            <Text
              key={d.label}
              x={(xScale(d.label) ?? 0) + barWidth / 2}
              y={innerHeight + 24}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={12}
              fontWeight={500}
            >
              {d.label}
            </Text>
          ))}
        </Group>
      </svg>
    </div>
  );
}
